import { AnimatedContentLoader, Button } from '@components'
import { useBusinessContext } from '@context'
import { useUnpaidInvoices } from '@root/query/hooks'
import { fetchCustomerPortalSession } from '@root/query/subscriptions'
import { formatCurrency, showFeaturebase } from '@utils'
import React, { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { FaExternalLinkAlt } from 'react-icons/fa'
import styled from 'styled-components'

export const StripeUnpaidBillingPanel: React.FC = () => {
  const [t] = useTranslation()
  const { slug } = useBusinessContext()
  const { data, isLoading, isError } = useUnpaidInvoices(slug)
  const [isOpeningPortal, setIsOpeningPortal] = useState(false)

  const openCustomerPortal = async () => {
    setIsOpeningPortal(true)
    try {
      const session = await fetchCustomerPortalSession({ slug })
      window.open(session.url, '_blank', 'noopener,noreferrer')
    } finally {
      setIsOpeningPortal(false)
    }
  }

  const currency = data?.currency ?? 'EUR'

  return (
    <Panel>
      <PanelHeader>
        <h2>{t('billingLock.stripeUnpaid.invoicesTitle')}</h2>
        <p>{t('billingLock.stripeUnpaid.description')}</p>
      </PanelHeader>

      <AnimatedContentLoader
        isLoading={isLoading}
        isError={isError}
        errorDescription={t('general.error')}
      >
        {data && (
          <>
            <Summary>
              <span>{t('billingLock.stripeUnpaid.invoiceCount', { count: data.count })}</span>
              <strong>
                {t('billingLock.stripeUnpaid.invoiceTotal', {
                  amount: formatCurrency(data.total, null, currency)
                })}
              </strong>
            </Summary>

            {data.invoices.length > 0 && (
              <InvoiceTable>
                <thead>
                  <tr>
                    <th>{t('billingLock.stripeUnpaid.invoiceDateColumn')}</th>
                    <th>{t('billingLock.stripeUnpaid.invoiceAmountColumn')}</th>
                    <th>{t('billingLock.stripeUnpaid.invoiceStatusColumn')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.invoices.map(invoice => (
                    <tr key={invoice.id}>
                      <td>{invoice.created}</td>
                      <td>{formatCurrency(invoice.amount, null, invoice.currency || currency)}</td>
                      <td>
                        {invoice.status === 'draft'
                          ? t('billingLock.stripeUnpaid.invoiceStatusDraft')
                          : t('billingLock.stripeUnpaid.invoiceStatusUnpaid')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </InvoiceTable>
            )}
          </>
        )}
      </AnimatedContentLoader>

      <Guidance>
        <p>{t('billingLock.stripeUnpaid.portalGuidance')}</p>
        <Button
          type="button"
          intent="success"
          icon={<FaExternalLinkAlt />}
          showSpinner={isOpeningPortal}
          disabled={isOpeningPortal}
          onClick={() => void openCustomerPortal()}
        >
          {t('billingLock.stripeUnpaid.payInPortalButton')}
        </Button>
      </Guidance>

      <HelperText>
        <Trans
          i18nKey="billingLock.stripeUnpaid.portalMissingInvoicesHelp"
          components={{
            supportLink: <SupportLink type="button" onClick={() => showFeaturebase('messages')} />,
            emailLink: <a href={t('link.email')} />
          }}
        />
      </HelperText>
    </Panel>
  )
}

const Panel = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md}rem;
  width: 100%;
  text-align: left;
`

const PanelHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs}rem;

  h2,
  p {
    margin: 0;
  }

  h2 {
    font-size: ${({ theme }) => theme.fontSize.lg}rem;
    color: ${({ theme }) => theme.colors.neutralBlack};
  }

  p {
    color: ${({ theme }) => theme.colors.metalGray};
    font-size: ${({ theme }) => theme.fontSize.md}rem;
    line-height: 1.6;
  }
`

const Summary = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  padding: ${({ theme }) => theme.spacing.md}rem;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  background: ${({ theme }) => theme.colors.neutralGray};
  color: ${({ theme }) => theme.colors.neutralBlack};
`

const InvoiceTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;

  th,
  td {
    padding: ${({ theme }) => theme.spacing.sm}rem;
    border-bottom: 1px solid ${({ theme }) => theme.colors.metalGray}33;
    text-align: left;
  }

  th {
    color: ${({ theme }) => theme.colors.metalGray};
    font-weight: 500;
  }
`

const Guidance = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.sm}rem;

  p {
    margin: 0;
    color: ${({ theme }) => theme.colors.metalGray};
    font-size: ${({ theme }) => theme.fontSize.md}rem;
    line-height: 1.6;
  }
`

const HelperText = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.metalGray};
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  line-height: 1.6;

  a,
  button {
    color: ${({ theme }) => theme.colors.primary};
    background: none;
    border: none;
    padding: 0;
    font: inherit;
    cursor: pointer;
    text-decoration: underline;
  }
`

const SupportLink = styled.button`
  color: ${({ theme }) => theme.colors.primary};
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  cursor: pointer;
  text-decoration: underline;
`
